import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Content,
  Icon,
  Main,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import {
  FhirEditScope,
  FhirReferenceOptionsProvider,
  FhirResourceScope,
  RenderResource,
  buildResolutionMap,
  useFhirEditScope,
} from '@eluve/fhir-renderer';
import { useMedplum } from '@eluve/medplum-client';

import { useLocationOptions } from '../data/locations';
import { deleteService, useService } from '../data/services';

/**
 * Service detail: read over REST, rendered and edited entirely by the
 * fhir-renderer (read card ↔ edit variant via the edit scope). This page
 * owns only the chrome — edit toggle, save/delete affordances, navigation.
 */

const EditBar: React.FC<{
  editing: boolean;
  onEditingChange: (next: boolean) => void;
  onCommitted: () => void;
}> = ({ editing, onEditingChange, onCommitted }) => {
  const { dirtyCount, commit, reset } = useFhirEditScope();
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  if (!editing) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => onEditingChange(true)}
        data-testid="edit-service"
      >
        <Icon name="Pencil" size="xs" />
        Edit
      </Button>
    );
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await commit();
      if (result.ok) {
        toast.success('Service updated.');
        onEditingChange(false);
        onCommitted();
      } else if (result.failures.some((f) => f.error.kind === 'conflict')) {
        toast.error(
          'This service changed since you loaded it. Refresh and re-apply your edits.',
        );
      } else {
        toast.error(
          `Couldn't save: ${result.failures[0]?.error.message ?? 'unknown error'}`,
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack direction="horizontal" gap={2} fullWidth={false}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          reset();
          onEditingChange(false);
        }}
      >
        Cancel
      </Button>
      <Button
        size="sm"
        disabled={dirtyCount === 0 || saving}
        onClick={() => void handleSave()}
        data-testid="save-service"
      >
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </Stack>
  );
};

export const ServiceDetailPage: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { client } = useMedplum();
  const toast = useToast();

  const { data: service, loading, error, refetch } = useService(serviceId);
  const map = useMemo(() => buildResolutionMap([]), []);
  const { options: locationOptions } = useLocationOptions();

  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const confirmDelete = async () => {
    if (!client || !serviceId) return;
    setConfirmingDelete(false);
    try {
      await deleteService(client, serviceId);
      toast.success('Service deleted');
      navigate('..');
    } catch (err) {
      toast.error(
        `Couldn't delete: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    }
  };

  return (
    <Main>
      <Content>
        <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
          <Link
            to=".."
            className="inline-flex items-center gap-1 text-sm text-text-tertiary hover:text-text-primary"
          >
            <Icon name="ChevronLeft" size="xs" />
            All services
          </Link>

          {loading ? (
            <Text size="sm" color="tertiary" weight="regular" className="mt-6">
              Loading service…
            </Text>
          ) : error || !service ? (
            <Text size="sm" color="tertiary" weight="regular" className="mt-6">
              Couldn't load this service.
            </Text>
          ) : (
            <Stack gap={3} align="stretch" className="mt-4">
              <FhirEditScope active={editing} transport={client ?? undefined}>
                <Stack
                  direction="horizontal"
                  gap={2}
                  align="center"
                  justify="end"
                >
                  {!editing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmingDelete(true)}
                      data-testid="delete-service"
                    >
                      <Icon name="Trash2" size="xs" />
                      Delete
                    </Button>
                  )}
                  <EditBar
                    editing={editing}
                    onEditingChange={setEditing}
                    onCommitted={refetch}
                  />
                </Stack>
                <FhirResourceScope map={map}>
                  <FhirReferenceOptionsProvider options={locationOptions}>
                    <RenderResource resource={service} />
                  </FhirReferenceOptionsProvider>
                </FhirResourceScope>
              </FhirEditScope>
            </Stack>
          )}
        </div>

        <AlertDialog
          open={confirmingDelete}
          onOpenChange={(open) => !open && setConfirmingDelete(false)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{service?.name}"?</AlertDialogTitle>
              <AlertDialogDescription>
                Patients won't be able to book it anymore. This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() => void confirmDelete()}
                data-testid="confirm-delete-service"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Content>
    </Main>
  );
};
